/**
 * ContractProjectTypesImportProvider
 *
 * Parses a spreadsheet upload (CSV / TSV / XLSX) describing ContractType or
 * ProjectType rows and routes each through the shared TypeComprehensionSaveProvider
 * — the exact same canonical type-save the editor uses — so an imported type gets
 * its prototype, default-date definitions, and ListDefault statuses just like one
 * created in the editor. The companion UI button in the Configuration App calls
 * `importSpreadsheetFile` with the picked File, or `importCsvText` /
 * `importMappedRows` directly when the caller already has parsed content.
 *
 * Recognized platform fields (header names — case-sensitive):
 *   Name, Hash, Description, Ordinal,
 *   AvailableStatusList, StatusesRequiringChangeOrder,
 *   AllowedProjectTypeIDs, DefaultDates
 *
 * List-shaped columns (AvailableStatusList,
 * StatusesRequiringChangeOrder, AllowedProjectTypeIDs) accept
 * pipe-delimited values: "Draft|Active|Closed". AllowedProjectTypeIDs accepts
 * readable ProjectType Hashes ("ROADWAY|BRIDGE") or numeric IDs; the save
 * provider resolves either to the ProjectType id it stores. DefaultDates accepts
 * pipe-delimited "Hash:Name" pairs: "NTP:Notice to Proceed|SUBCMP:Substantial Completion".
 *
 * CSV escaping follows the RFC 4180 subset commonly seen in agency
 * spreadsheets: commas allowed inside double-quoted fields, doubled quotes
 * escape a literal quote inside a quoted field.
 *
 * XLSX support uses the bundled `xlsx` (SheetJS) library. Note: the npm
 * package is unmaintained (CVE-2024-22363 — prototype pollution in parsing);
 * the maintained release is on https://cdn.sheetjs.com/. For our Configuration
 * App admin-only upload flow the risk surface is the same user's own data,
 * but it's worth tracking the CDN migration as a follow-up.
 */

const libFableServiceProviderBase = require('fable-serviceproviderbase');
/**
 * Resolve the xlsx (SheetJS) module at use-time. In the browser the library is
 * loaded via a separate `<script src="js/xlsx.full.min.js">` tag (see
 * `html/index.html`) so it lives on `window.XLSX`. In Node (unit tests) we
 * fall through to a plain `require('xlsx')`. Browserify is configured to
 * ignore the bare `require('xlsx')` call (see `.gulpfile-quackage-config.json`
 * `BrowserifyIgnore`), so the main app bundle stays small and free of the
 * Node-only paths inside the npm xlsx@0.18.5 package that previously broke
 * the top-level module evaluation.
 *
 * @returns {any | null} the xlsx module, or null when neither path is available
 */
function _getXlsx()
{
	if (typeof window !== 'undefined' && window.XLSX)
	{
		return window.XLSX;
	}
	try
	{
		// eslint-disable-next-line global-require
		return require('xlsx');
	}
	catch (pError)
	{
		return null;
	}
}

// List columns become text on `_DisplaySettings` (the editor's display shape) so the
// imported record runs through the exact same type-save path as the editor. Status
// lists are newline-joined (TextArea form); AllowedProjectTypeIDs is comma-joined
// (multi-select form). The save provider's fromDisplay() then normalizes them.
const STATUS_LIST_KEYS = new Set([
	'AvailableStatusList',
	'StatusesRequiringChangeOrder',
]);
const CSV_LIST_KEYS = new Set([
	'AllowedProjectTypeIDs',
]);
const TOP_LEVEL_KEYS = new Set([ 'Name', 'Hash', 'Description', 'Ordinal' ]);

/**
 * Split a single CSV/TSV line into fields, honoring double-quoted
 * sub-strings (with "" → " escape).
 *
 * @param {string} pLine
 * @param {string} pDelimiter
 * @returns {Array<string>}
 */
function splitLine(pLine, pDelimiter)
{
	const tmpFields = [];
	let tmpBuffer = '';
	let tmpInQuotes = false;
	for (let i = 0; i < pLine.length; i++)
	{
		const tmpChar = pLine.charAt(i);
		if (tmpInQuotes)
		{
			if (tmpChar === '"')
			{
				if (i + 1 < pLine.length && pLine.charAt(i + 1) === '"')
				{
					tmpBuffer += '"';
					i += 1;
				}
				else
				{
					tmpInQuotes = false;
				}
			}
			else
			{
				tmpBuffer += tmpChar;
			}
		}
		else if (tmpChar === '"')
		{
			tmpInQuotes = true;
		}
		else if (tmpChar === pDelimiter)
		{
			tmpFields.push(tmpBuffer);
			tmpBuffer = '';
		}
		else
		{
			tmpBuffer += tmpChar;
		}
	}
	tmpFields.push(tmpBuffer);
	return tmpFields;
}

/**
 * Parse a pipe-delimited DefaultDates cell into structured records.
 *
 * @param {string} pCell - e.g. "NTP:Notice to Proceed|SUBCMP:Substantial Completion"
 * @returns {Array<{Hash: string, Name: string}>}
 */
function parseDefaultDatesCell(pCell)
{
	if (!pCell || typeof pCell !== 'string')
	{
		return [];
	}
	return pCell.split('|').reduce((pAccum, pEntry) =>
	{
		const tmpTrimmed = pEntry.trim();
		if (!tmpTrimmed)
		{
			return pAccum;
		}
		const tmpColonIdx = tmpTrimmed.indexOf(':');
		if (tmpColonIdx === -1)
		{
			pAccum.push({ Hash: tmpTrimmed, Name: tmpTrimmed });
		}
		else
		{
			pAccum.push({
				Hash: tmpTrimmed.substring(0, tmpColonIdx).trim(),
				Name: tmpTrimmed.substring(tmpColonIdx + 1).trim(),
			});
		}
		return pAccum;
	}, []);
}

/**
 * Parse a CSV/TSV text blob into a `{ headers, rows }` shape where each row
 * is a plain string array aligned to the headers. This is the format the
 * column-mapping UI consumes — no platform-field interpretation happens here.
 *
 * @param {string} pText - Raw file content.
 * @param {{ Delimiter?: string }} [pOptions]
 * @returns {{ headers: Array<string>, rows: Array<Array<string>> }}
 */
function parseCsvTextToRows(pText, pOptions)
{
	if (typeof pText !== 'string' || pText.length === 0)
	{
		return { headers: [], rows: [] };
	}
	const tmpDelimiter = (pOptions && pOptions.Delimiter) || (pText.indexOf('\t') !== -1 && pText.indexOf(',') === -1 ? '\t' : ',');
	const tmpLines = pText.split(/\r?\n/).filter((pLine) => pLine.length > 0);
	if (tmpLines.length < 1)
	{
		return { headers: [], rows: [] };
	}
	const tmpHeaders = splitLine(tmpLines[0], tmpDelimiter).map((pHeader) => pHeader.trim());
	const tmpRows = [];
	for (let i = 1; i < tmpLines.length; i++)
	{
		const tmpFields = splitLine(tmpLines[i], tmpDelimiter);
		const tmpRow = [];
		for (let j = 0; j < tmpHeaders.length; j++)
		{
			tmpRow.push((tmpFields[j] || '').trim());
		}
		tmpRows.push(tmpRow);
	}
	return { headers: tmpHeaders, rows: tmpRows };
}

/**
 * Parse an XLSX ArrayBuffer (first sheet only) into the same `{ headers, rows }`
 * shape `parseCsvTextToRows` returns. Returns an empty result when the xlsx
 * library is not present at runtime (e.g. in a Node-only mocha run that hasn't
 * loaded the bundled module).
 *
 * @param {ArrayBuffer | Uint8Array} pArrayBuffer
 * @returns {{ headers: Array<string>, rows: Array<Array<string>> }}
 */
function parseXlsxBufferToRows(pArrayBuffer)
{
	const tmpXLSX = _getXlsx();
	if (!tmpXLSX || !pArrayBuffer)
	{
		return { headers: [], rows: [] };
	}
	const tmpWorkbook = tmpXLSX.read(pArrayBuffer, { type: 'array' });
	const tmpSheetName = tmpWorkbook.SheetNames[0];
	if (!tmpSheetName)
	{
		return { headers: [], rows: [] };
	}
	const tmpSheet = tmpWorkbook.Sheets[tmpSheetName];
	// `header: 1` gives us arrays-of-arrays — same shape as parseCsvTextToRows.
	// `raw: false` returns strings (numbers come back formatted, dates ISO-ish).
	// `defval: ''` keeps short rows aligned with header length.
	const tmpMatrix = tmpXLSX.utils.sheet_to_json(tmpSheet, { header: 1, raw: false, defval: '' });
	if (!Array.isArray(tmpMatrix) || tmpMatrix.length === 0)
	{
		return { headers: [], rows: [] };
	}
	const tmpHeaders = (tmpMatrix[0] || []).map((pHeader) => String(pHeader == null ? '' : pHeader).trim());
	const tmpRows = [];
	for (let i = 1; i < tmpMatrix.length; i++)
	{
		const tmpRow = [];
		for (let j = 0; j < tmpHeaders.length; j++)
		{
			const tmpCell = tmpMatrix[i] && tmpMatrix[i][j];
			tmpRow.push(String(tmpCell == null ? '' : tmpCell).trim());
		}
		// Skip rows that are entirely blank (Excel often emits trailing empties).
		if (tmpRow.some((pCell) => pCell.length > 0))
		{
			tmpRows.push(tmpRow);
		}
	}
	return { headers: tmpHeaders, rows: tmpRows };
}

/**
 * Marshal one `{ headers, rows }` snapshot through a column-mapping into
 * editor display-shaped records (`{ Name, Hash, Description, Ordinal,
 * _DisplaySettings: {...} }`) — the shape TypeComprehensionSaveProvider consumes.
 * The mapping maps **source header → platform field name**. Source columns mapped
 * to the empty string or to a value not in the recognized platform-field set are
 * skipped, so callers can pass a partial mapping.
 *
 * @param {{ headers: Array<string>, rows: Array<Array<string>> }} pSheet
 * @param {Object<string, string>} [pColumnMapping] - source-header → platform-field. Defaults to identity.
 * @returns {Array<Object>}
 */
function applyMappingToRecords(pSheet, pColumnMapping)
{
	if (!pSheet || !Array.isArray(pSheet.headers) || !Array.isArray(pSheet.rows))
	{
		return [];
	}
	const tmpMapping = pColumnMapping || {};
	const tmpResolvedFields = pSheet.headers.map((pSource) =>
	{
		// Empty / explicitly-null mapping value → skip this column.
		// Missing mapping entry → identity (source header is the platform field).
		const tmpExplicit = Object.prototype.hasOwnProperty.call(tmpMapping, pSource) ? tmpMapping[pSource] : pSource;
		if (typeof tmpExplicit !== 'string' || tmpExplicit.length === 0)
		{
			return null;
		}
		return tmpExplicit;
	});
	const tmpRecords = [];
	for (const tmpRow of pSheet.rows)
	{
		const tmpRecord = {};
		const tmpDisplay = {};
		let tmpHasDisplay = false;
		for (let j = 0; j < tmpResolvedFields.length; j++)
		{
			const tmpField = tmpResolvedFields[j];
			if (!tmpField)
			{
				continue;
			}
			const tmpRaw = (tmpRow[j] == null ? '' : String(tmpRow[j])).trim();
			if (tmpRaw === '')
			{
				continue;
			}
			if (TOP_LEVEL_KEYS.has(tmpField))
			{
				tmpRecord[tmpField] = tmpField === 'Ordinal' ? Number(tmpRaw) : tmpRaw;
			}
			else if (STATUS_LIST_KEYS.has(tmpField))
			{
				// Status lists are comma-joined: the editor's status fields are Select2Multiple
				// (CSV) and the save path's statusDisplayToPayload() splits them with csvToList()
				// on commas. Joining with anything else (e.g. newlines) leaves the whole list as
				// one lumped status on the /Statuses endpoint.
				tmpDisplay[tmpField] = tmpRaw.split('|').map((pPart) => pPart.trim()).filter((pPart) => pPart.length > 0).join(',');
				tmpHasDisplay = true;
			}
			else if (CSV_LIST_KEYS.has(tmpField))
			{
				// Comma-separated text for the multi-select display form.
				tmpDisplay[tmpField] = tmpRaw.split('|').map((pPart) => pPart.trim()).filter((pPart) => pPart.length > 0).join(',');
				tmpHasDisplay = true;
			}
			else if (tmpField === 'DefaultDates')
			{
				tmpDisplay.DefaultDates = parseDefaultDatesCell(tmpRaw);
				tmpHasDisplay = true;
			}
		}
		if (tmpHasDisplay)
		{
			tmpRecord._DisplaySettings = tmpDisplay;
		}
		if (tmpRecord.Name || tmpRecord.Hash)
		{
			tmpRecords.push(tmpRecord);
		}
	}
	return tmpRecords;
}

/**
 * The set of platform field names the import knows how to interpret. The
 * column-mapping UI uses this as its target-field dropdown.
 */
const PLATFORM_FIELD_NAMES = Object.freeze([
	'Name',
	'Hash',
	'Description',
	'Ordinal',
	'AvailableStatusList',
	'StatusesRequiringChangeOrder',
	'AllowedProjectTypeIDs',
	'DefaultDates',
]);

/**
 * The ordered columns for a blank import template, per entity. Each list is the
 * subset of PLATFORM_FIELD_NAMES the corresponding save path actually consumes:
 *   - ProjectType (saveProjectType): no AllowedProjectTypeIDs.
 *   - ContractType (saveContractType): no Ordinal.
 * Both carry DefaultDates. Downloading these as headers gives a user a spreadsheet
 * whose columns the importer recognizes one-for-one (see suggestColumnMapping).
 */
const TEMPLATE_COLUMNS = Object.freeze({
	ProjectType: Object.freeze([
		'Name', 'Hash', 'Description', 'Ordinal',
		'AvailableStatusList', 'StatusesRequiringChangeOrder', 'DefaultDates',
	]),
	ContractType: Object.freeze([
		'Name', 'Hash', 'Description',
		'AvailableStatusList', 'StatusesRequiringChangeOrder', 'AllowedProjectTypeIDs', 'DefaultDates',
	]),
});

/**
 * The ordered template columns for an entity. Unknown entities default to the
 * ProjectType shape.
 *
 * @param {'ContractType' | 'ProjectType'} pEntity
 * @returns {Array<string>}
 */
function templateColumnsForEntity(pEntity)
{
	return (pEntity === 'ContractType') ? TEMPLATE_COLUMNS.ContractType.slice() : TEMPLATE_COLUMNS.ProjectType.slice();
}

/**
 * Per-column guidance shown on the Instructions sheet. Keyed by platform field; the
 * Instructions table is generated from the entity's actual column list so it can never
 * drift from what the importer accepts.
 */
const COLUMN_DOC = Object.freeze({
	Name: { required: 'Required', format: 'Plain text — the display name', example: 'Roadway' },
	Hash: { required: 'Required', format: 'Short stable code; unique within your customer', example: 'ROADWAY' },
	Description: { required: 'Optional', format: 'Plain text', example: 'Road construction projects' },
	Ordinal: { required: 'Optional', format: 'Whole number (sort order)', example: '1' },
	AvailableStatusList: { required: 'Optional', format: 'Status names separated by a pipe "|"', example: 'Draft|Active|Closed' },
	StatusesRequiringChangeOrder: { required: 'Optional', format: 'Subset of Available Statuses, separated by "|"', example: 'Active|Closed' },
	AllowedProjectTypeIDs: { required: 'Optional', format: 'Project Type Hashes (readable codes) or IDs, separated by "|"', example: 'ROADWAY|BRIDGE' },
	DefaultDates: { required: 'Optional', format: '"Code:Label" pairs separated by "|"', example: 'NTP:Notice to Proceed|SUBCMP:Substantial Completion' },
});

/**
 * Build the rows (array-of-arrays) for the Instructions sheet: a short how-to, a
 * per-column reference generated from the entity's columns, and a few notes. Pure —
 * no xlsx dependency — so it is unit-testable on its own.
 *
 * @param {'ContractType' | 'ProjectType'} pEntity
 * @returns {Array<Array<string>>}
 */
function buildTemplateInstructions(pEntity)
{
	const tmpIsContract = (pEntity === 'ContractType');
	const tmpLabel = tmpIsContract ? 'Contract' : 'Project';
	const tmpDataSheet = tmpIsContract ? 'ContractTypes' : 'ProjectTypes';
	const tmpRows = [
		[ `${tmpLabel} Type bulk import — instructions` ],
		[ '' ],
		[ 'How to use' ],
		[ `1. Enter your ${tmpLabel.toLowerCase()} types on the "${tmpDataSheet}" tab — one per row, starting in row 2 (under the headers).` ],
		[ '2. Do not rename, reorder, or delete the column headers in row 1 of that tab.' ],
		[ '3. Leave this Instructions tab as-is — the importer only reads the first tab.' ],
		[ '4. In list columns, separate multiple values with a pipe "|" (not commas).' ],
		[ `5. Save the file, then on the dashboard use the ${tmpLabel} Types card → "Import from Spreadsheet".` ],
		[ '' ],
		[ 'Columns', 'Required', 'Format', 'Example' ],
	];
	for (const tmpColumn of templateColumnsForEntity(pEntity))
	{
		const tmpDoc = COLUMN_DOC[tmpColumn] || { required: 'Optional', format: '', example: '' };
		tmpRows.push([ tmpColumn, tmpDoc.required, tmpDoc.format, tmpDoc.example ]);
	}
	tmpRows.push(
		[ '' ],
		[ 'Notes' ],
		[ '- Hash identifies the type; keep it short, stable, and unique within your customer.' ],
		[ '- "Statuses Requiring Change Order" should use names that also appear in "Available Statuses".' ],
		[ '- Default Dates use "Code:Label" pairs; a value with no colon is used as both the code and the label.' ],
		[ '- Empty optional cells are simply skipped.' ],
		[ '- On upload you get a column-mapping preview, so you can confirm before anything is saved.' ],
	);
	return tmpRows;
}

/**
 * Build the entity's import-template XLSX workbook as bytes ready for a Blob
 * download, or null when the xlsx library is unavailable at runtime (caller should
 * fall back to CSV).
 *
 * The workbook has TWO sheets:
 *   - sheet 0: the data sheet (header row only) — the sheet the user fills and the
 *     importer reads (the importer only ever reads the first sheet), and
 *   - sheet 1: an "Instructions" sheet the importer ignores.
 * The data sheet is kept pristine (just headers) so its integrity as the upload
 * target is never affected by the guidance.
 *
 * @param {'ContractType' | 'ProjectType'} pEntity
 * @returns {Uint8Array | null}
 */
function buildTemplateWorkbookBytes(pEntity)
{
	const tmpXLSX = _getXlsx();
	if (!tmpXLSX)
	{
		return null;
	}
	const tmpColumns = templateColumnsForEntity(pEntity);
	const tmpDataSheetName = (pEntity === 'ContractType') ? 'ContractTypes' : 'ProjectTypes';

	// Sheet 0: the upload target — header row only, with readable column widths.
	const tmpDataSheet = tmpXLSX.utils.aoa_to_sheet([ tmpColumns ]);
	tmpDataSheet['!cols'] = tmpColumns.map((pColumn) => ({ wch: Math.max(16, pColumn.length + 2) }));

	// Sheet 1: instructions (ignored by the importer, which only reads the first sheet).
	const tmpInstructionsSheet = tmpXLSX.utils.aoa_to_sheet(buildTemplateInstructions(pEntity));
	tmpInstructionsSheet['!cols'] = [ { wch: 30 }, { wch: 12 }, { wch: 52 }, { wch: 46 } ];

	const tmpBook = tmpXLSX.utils.book_new();
	// Data sheet is appended first so it is sheet 0 — the workbook opens on it (the sheet
	// to fill) and, critically, the importer only ever reads the first sheet. The
	// Instructions tab sits beside it for reference and is ignored on import.
	tmpXLSX.utils.book_append_sheet(tmpBook, tmpDataSheet, tmpDataSheetName);
	tmpXLSX.utils.book_append_sheet(tmpBook, tmpInstructionsSheet, 'Instructions');

	return tmpXLSX.write(tmpBook, { bookType: 'xlsx', type: 'array' });
}

/**
 * Build an empty (header-row-only) CSV string for the entity's import template.
 * Fallback for when the xlsx runtime is not present.
 *
 * @param {'ContractType' | 'ProjectType'} pEntity
 * @returns {string}
 */
function buildTemplateCsv(pEntity)
{
	return templateColumnsForEntity(pEntity).join(',') + '\r\n';
}

/**
 * Describe the downloadable blank template for an entity: filename, MIME type,
 * and content. Prefers XLSX; falls back to CSV when xlsx is unavailable.
 *
 * @param {'ContractType' | 'ProjectType'} pEntity
 * @returns {{ filename: string, mimeType: string, content: (Uint8Array|string) }}
 */
function buildTemplateDescriptor(pEntity)
{
	const tmpLabel = (pEntity === 'ContractType') ? 'ContractType' : 'ProjectType';
	const tmpBytes = buildTemplateWorkbookBytes(pEntity);
	if (tmpBytes)
	{
		return {
			filename: `${tmpLabel}-Import-Template.xlsx`,
			mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			content: tmpBytes,
		};
	}
	return {
		filename: `${tmpLabel}-Import-Template.csv`,
		mimeType: 'text/csv',
		content: buildTemplateCsv(pEntity),
	};
}

/**
 * Build a default column-mapping by matching source headers against platform
 * field names case-insensitively. Unknown headers map to the empty string
 * (= skip). Drives the auto-population of the mapping UI.
 *
 * @param {Array<string>} pHeaders
 * @returns {Object<string, string>}
 */
function suggestColumnMapping(pHeaders)
{
	const tmpResult = {};
	if (!Array.isArray(pHeaders))
	{
		return tmpResult;
	}
	const tmpLowerCaseMap = {};
	for (const tmpField of PLATFORM_FIELD_NAMES)
	{
		tmpLowerCaseMap[tmpField.toLowerCase()] = tmpField;
	}
	for (const tmpHeader of pHeaders)
	{
		if (typeof tmpHeader !== 'string' || tmpHeader.length === 0)
		{
			continue;
		}
		tmpResult[tmpHeader] = tmpLowerCaseMap[tmpHeader.toLowerCase()] || '';
	}
	return tmpResult;
}

/**
 * Back-compat wrapper. Parse CSV/TSV text into Meadow-shaped records
 * directly — equivalent to `parseCsvTextToRows` + `applyMappingToRecords`
 * with an identity mapping.
 *
 * @param {string} pText
 * @param {{ Delimiter?: string }} [pOptions]
 * @returns {Array<Object>}
 */
function parseCsvText(pText, pOptions)
{
	return applyMappingToRecords(parseCsvTextToRows(pText, pOptions), null);
}

/**
 * Provider class. The static parsers above are exposed for unit tests
 * and don't require a Fable instance.
 */
class ContractProjectTypesImportProvider extends libFableServiceProviderBase
{
	/**
	 * @param {*} pFable - The fable instance.
	 * @param {*} [pOptions] - Provider options.
	 * @param {string} [pServiceHash] - Fable service hash for this instance.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.pict = this.fable;
		this.serviceType = 'ContractProjectTypesImportProvider';
	}

	/**
	 * @returns {*} the shared TypeComprehensionSaveProvider fable service, or null.
	 */
	get _typeSaveProvider()
	{
		return (this.fable && this.fable.TypeComprehensionSaveProvider)
			|| (this.fable && this.fable.serviceManager && this.fable.serviceManager.instantiateServiceProvider('TypeComprehensionSaveProvider', {}))
			|| null;
	}

	/**
	 * @returns {number} the logged-in user's customer id (stamped onto imported types
	 * so the comprehension's child rows inherit the right customer), or 0 if unknown.
	 */
	get _currentIDCustomer()
	{
		const tmpUser = this.fable && this.fable.AppData && this.fable.AppData.CurrentUser;
		return (tmpUser && (Number(tmpUser.IDCustomer) || Number(tmpUser.CustomerID))) || 0;
	}

	/**
	 * Describe the downloadable blank import template for an entity (filename,
	 * MIME type, content). The dashboard view wraps `content` in a Blob and
	 * triggers the browser download.
	 *
	 * @param {'ContractType' | 'ProjectType'} pEntity
	 * @returns {{ filename: string, mimeType: string, content: (Uint8Array|string) }}
	 */
	buildTemplateDescriptor(pEntity)
	{
		return buildTemplateDescriptor(pEntity);
	}

	/**
	 * Parse CSV/TSV text into records and save them via the canonical type-save.
	 * Kept for back-compat — newer callers should drive the parse/mapping/import via
	 * the staged methods (parseSpreadsheetFile → applyMappingToRecords → import).
	 *
	 * @param {string} pCsvText - The raw upload content.
	 * @param {'ContractType' | 'ProjectType'} pEntity - The target type entity.
	 * @param {(error: Error | null, summary: { Count: number, Records: Array<Object> }) => void} fCallback
	 */
	importCsvText(pCsvText, pEntity, fCallback)
	{
		try
		{
			const tmpRecords = parseCsvText(pCsvText);
			return this._saveRecords(tmpRecords, pEntity, fCallback);
		}
		catch (pError)
		{
			return fCallback(pError, { Count: 0, Records: [] });
		}
	}

	/**
	 * Parse a picked File (CSV / TSV / XLSX) into the `{ headers, rows }`
	 * shape the column-mapping UI consumes. Dispatches on the file extension
	 * — `.xlsx` reads as ArrayBuffer + SheetJS; everything else reads as text
	 * and goes through the CSV parser (which auto-detects tab-delimited).
	 *
	 * @param {File} pFile
	 * @param {(error: Error | null, sheet: { headers: Array<string>, rows: Array<Array<string>> }) => void} fCallback
	 */
	parseSpreadsheetFile(pFile, fCallback)
	{
		if (!pFile || typeof pFile.name !== 'string')
		{
			return fCallback(new Error('No file provided to parseSpreadsheetFile.'), { headers: [], rows: [] });
		}
		const tmpIsXlsx = /\.xlsx$/i.test(pFile.name) ||
			pFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		const tmpReader = new FileReader();
		tmpReader.onload = (pEvent) =>
		{
			try
			{
				if (tmpIsXlsx)
				{
					const tmpBuffer = pEvent?.target?.result;
					const tmpSheet = parseXlsxBufferToRows(tmpBuffer);
					if (tmpSheet.headers.length === 0)
					{
						return fCallback(new Error('Could not read the workbook. The xlsx parser is unavailable or the file is empty.'), tmpSheet);
					}
					return fCallback(null, tmpSheet);
				}
				const tmpText = String(pEvent?.target?.result || '');
				return fCallback(null, parseCsvTextToRows(tmpText));
			}
			catch (pError)
			{
				return fCallback(pError, { headers: [], rows: [] });
			}
		};
		tmpReader.onerror = () => fCallback(new Error('Failed to read the selected file.'), { headers: [], rows: [] });
		if (tmpIsXlsx)
		{
			tmpReader.readAsArrayBuffer(pFile);
		}
		else
		{
			tmpReader.readAsText(pFile);
		}
	}

	/**
	 * Apply a column mapping to a previously-parsed `{ headers, rows }` and
	 * POST the resulting records. Used by the column-mapping UI after the
	 * user confirms.
	 *
	 * @param {{ headers: Array<string>, rows: Array<Array<string>> }} pSheet
	 * @param {Object<string, string>} pColumnMapping
	 * @param {'ContractType' | 'ProjectType'} pEntity
	 * @param {(error: Error | null, summary: { Count: number, Records: Array<Object> }) => void} fCallback
	 * @param {(done: number, total: number) => void} [fProgress] - called before the first save
	 *   and after each record so the UI can show import progress.
	 */
	importMappedRows(pSheet, pColumnMapping, pEntity, fCallback, fProgress)
	{
		try
		{
			const tmpRecords = applyMappingToRecords(pSheet, pColumnMapping);
			return this._saveRecords(tmpRecords, pEntity, fCallback, fProgress);
		}
		catch (pError)
		{
			return fCallback(pError, { Count: 0, Records: [] });
		}
	}

	/**
	 * Shared save loop. Routes each parsed record through the canonical
	 * TypeComprehensionSaveProvider (build comprehension -> push -> prototype pointer
	 * -> statuses), so imported types are identical to editor-created ones — prototype,
	 * default-date definitions, and ListDefault statuses included. Runs sequentially
	 * (each save is a multi-step comprehension push) and is first-error-wins: a failed
	 * record is recorded but the remaining records still import.
	 *
	 * @private
	 * @param {Array<Object>} pRecords - editor display-shaped records.
	 * @param {'ContractType' | 'ProjectType'} pEntity
	 * @param {(error: Error | null, summary: { Count: number, Records: Array<Object> }) => void} fCallback
	 * @param {(done: number, total: number) => void} [fProgress] - reported before the first save
	 *   (0/total) and after each record, so the UI can show a live progress indicator. Because the
	 *   loop awaits between records, these updates render between saves.
	 * @returns {Promise<void>}
	 */
	async _saveRecords(pRecords, pEntity, fCallback, fProgress)
	{
		const tmpReportProgress = (pDone, pTotal) =>
		{
			if (typeof fProgress === 'function')
			{
				fProgress(pDone, pTotal);
			}
		};
		if (!Array.isArray(pRecords) || pRecords.length === 0)
		{
			return fCallback(null, { Count: 0, Records: [] });
		}
		const tmpProvider = this._typeSaveProvider;
		if (!tmpProvider)
		{
			return fCallback(new Error('TypeComprehensionSaveProvider is not available — cannot import types.'), { Count: 0, Records: pRecords });
		}
		const tmpTotal = pRecords.length;
		const tmpIDCustomer = this._currentIDCustomer;
		let tmpFirstError = null;
		let tmpDone = 0;
		tmpReportProgress(0, tmpTotal);
		for (const tmpRecord of pRecords)
		{
			// Stamp the current customer so the comprehension's child rows inherit it.
			if (tmpIDCustomer && !tmpRecord.IDCustomer)
			{
				tmpRecord.IDCustomer = tmpIDCustomer;
			}
			try
			{
				// Import is always create/upsert semantics (no prune on re-import); the
				// deterministic Hash-derived GUIDs make a re-import idempotent.
				if (pEntity === 'ContractType')
				{
					await tmpProvider.saveContractType(tmpRecord, true);
				}
				else
				{
					await tmpProvider.saveProjectType(tmpRecord, true);
				}
			}
			catch (pError)
			{
				if (!tmpFirstError)
				{
					tmpFirstError = pError;
				}
				this.pict.log.error(`Import: failed to save ${pEntity} "${tmpRecord.Name || tmpRecord.Hash}".`, { Error: pError && pError.message });
			}
			tmpDone += 1;
			tmpReportProgress(tmpDone, tmpTotal);
		}
		return fCallback(tmpFirstError, { Count: pRecords.length, Records: pRecords });
	}
}

ContractProjectTypesImportProvider.default_configuration = {
	Name: 'ContractProjectTypesImportProvider',
	ProviderType: 'ContractProjectTypesImportProvider',
};

module.exports = ContractProjectTypesImportProvider;
module.exports.parseCsvText = parseCsvText;
module.exports.parseCsvTextToRows = parseCsvTextToRows;
module.exports.parseXlsxBufferToRows = parseXlsxBufferToRows;
module.exports.applyMappingToRecords = applyMappingToRecords;
module.exports.suggestColumnMapping = suggestColumnMapping;
module.exports.parseDefaultDatesCell = parseDefaultDatesCell;
module.exports.splitLine = splitLine;
module.exports.PLATFORM_FIELD_NAMES = PLATFORM_FIELD_NAMES;
module.exports.TEMPLATE_COLUMNS = TEMPLATE_COLUMNS;
module.exports.templateColumnsForEntity = templateColumnsForEntity;
module.exports.buildTemplateInstructions = buildTemplateInstructions;
module.exports.buildTemplateWorkbookBytes = buildTemplateWorkbookBytes;
module.exports.buildTemplateCsv = buildTemplateCsv;
module.exports.buildTemplateDescriptor = buildTemplateDescriptor;
